// ============================================================================
// SessionsCalendar — the dashboard centrepiece (owner-requested).
// ----------------------------------------------------------------------------
// A real, usable calendar of ALL clients' scheduled sessions, with:
//   • MONTH view (6×7 grid) AND WEEK view (7 day columns) — toggle between them
//   • sessions rendered as events on the correct day (client name + time)
//   • a colour legend per session type
//   • clicking any day opens a detail panel listing that day's sessions
//   • prev / next / "Today" navigation that respects the active view
//   • responsive: month grid → scannable cells on desktop/tablet; on phone the
//     month becomes a tap-first heat grid with a count, week stays columnar
//   • keyboard accessible day cells + reduced-motion aware transitions
// ============================================================================
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Avatar, Badge, Button, Card } from "../components";
import {
  IconCalendarBlank,
} from "./calendarIcons";
import {
  IconChevronLeft,
  IconChevronRight,
  IconLayoutGrid,
  IconMapPin,
} from "../components/icons";
import {
  addDays,
  addMonths,
  formatDayLong,
  formatTime,
  isSameDay,
  monthGrid,
  monthTitle,
  startOfWeek,
  weekdayLabel,
  weekdayLabels,
  weekDays,
  weekRangeTitle,
} from "../lib/date";
import {
  activitiesOnDay,
  mealMarkersOnDay,
  sessionsOnDay,
  weeklyGoalsOnWeek,
  useDashboardCtx,
} from "./DashboardDataContext";
import type {
  ActivityVM,
  MealMarkerVM,
  SessionVM,
  WeeklyGoalBannerVM,
} from "./useDashboardData";
import { ENTRY_STYLE, SESSION_STYLE } from "./sessionStyle";
import { DaySummary, type DaySummaryItem } from "./DaySummary";
import { localDayInZone } from "../lib/datetime";
import { useAccount } from "../hooks";
import { SessionDetailSheet } from "./SessionDetailSheet";
import { ActivityDetailSheet } from "./ActivityDetailSheet";
import { MealMarkerSheet } from "./MealMarkerSheet";
import { CalendarLegend } from "./CalendarLegend";
import { AddEntrySheet } from "./AddEntrySheet";
import { IconPlus } from "../components/icons";
import { cn } from "../lib/cn";

type View = "month" | "week";
type SessionWithClient = SessionVM;
/** Open a session's detail/edit sheet from any calendar surface. */
type PickSession = (session: SessionVM) => void;
/** Open an activity's detail sheet from any calendar surface. */
type PickActivity = (activity: ActivityVM) => void;
/** Open a meal marker's day-summary sheet from any calendar surface. */
type PickMeal = (marker: MealMarkerVM) => void;
/** Open the add-on-day sheet for a given calendar day (spec §B). */
type AddOnDay = (day: Date) => void;

/** Rank used to break ties so the merged order is stable across the grid. */
function entryRank(e: CalendarEntry): number {
  return e.entry === "session" ? 0 : e.entry === "activity" ? 1 : 2;
}

/** Map merged calendar entries to DaySummary items for the phone chip row. */
function toSummaryItems(
  items: CalendarEntry[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (...args: any[]) => any,
): DaySummaryItem[] {
  return items.map((e) => {
    if (e.entry === "session") {
      return {
        provenance: "pt_session" as const,
        label: e.data.rawType || e.data.type || t("dashboard:calendar.session"),
      };
    }
    if (e.entry === "activity") {
      return {
        provenance: e.data.kind,
        label: e.data.title || e.data.type || t("dashboard:calendar.activity"),
      };
    }
    return {
      provenance: "meal" as const,
      // The all-clients trainer feed doesn't carry a per-day meal count
      // (mealMarkerToVM -> count: 0), so fall back to a plain "Meals" label
      // rather than rendering a misleading "0 meals" chip.
      label:
        e.data.count > 0
          ? t("dashboard:calendar.mealCount", { count: e.data.count })
          : t("dashboard:calendar.meals"),
    };
  });
}

/**
 * A merged calendar entry: a PT session, a provenance-tagged activity, OR a
 * per-day meal marker. The grid renders all three, coloured by SESSION_STYLE
 * (sessions, by type) / ENTRY_STYLE (activities + meals, by provenance).
 * Discriminated by `entry`.
 */
type CalendarEntry =
  | { entry: "session"; data: SessionVM; startsAt: string }
  | { entry: "activity"; data: ActivityVM; startsAt: string }
  | { entry: "meal"; data: MealMarkerVM; startsAt: string };

/**
 * Time-sorted union of a day's sessions + activities + meal markers. Meals are
 * day-markers (startsAt = local midnight) so they sort first; ties break by rank
 * (session < activity < meal) for a stable grid order.
 */
function mergeEntries(
  sessions: SessionVM[],
  activities: ActivityVM[],
  meals: MealMarkerVM[] = [],
): CalendarEntry[] {
  const merged: CalendarEntry[] = [
    ...sessions.map(
      (s): CalendarEntry => ({ entry: "session", data: s, startsAt: s.startsAt }),
    ),
    ...activities.map(
      (a): CalendarEntry => ({ entry: "activity", data: a, startsAt: a.startsAt }),
    ),
    ...meals.map(
      (m): CalendarEntry => ({ entry: "meal", data: m, startsAt: m.startsAt }),
    ),
  ];
  return merged.sort((a, b) => {
    const c = a.startsAt.localeCompare(b.startsAt);
    if (c !== 0) return c;
    return entryRank(a) - entryRank(b);
  });
}

/** Fixed "today" for the lifetime of a render pass (stable cursor/selection). */
const TODAY = (() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
})();

export function SessionsCalendar() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const data = useDashboardCtx();
  const accountQuery = useAccount();
  const trainerTz = accountQuery.data?.timezone ?? null;
  const [view, setView] = useState<View>("month");
  const [cursor, setCursor] = useState<Date>(() => TODAY);
  const [selected, setSelected] = useState<Date>(() => TODAY);
  // The opened appointment is tracked by id so the detail sheet always reflects
  // the freshest VM after an optimistic/live-sync update (re-derived below).
  const [openSessionId, setOpenSessionId] = useState<number | null>(null);
  // The opened activity is tracked separately (same id-keyed live-sync posture).
  const [openActivityId, setOpenActivityId] = useState<number | null>(null);
  // The opened meal marker is tracked by its stable per-day id (re-derived from
  // the live feed so a cross-device meal log keeps the open summary fresh).
  const [openMealId, setOpenMealId] = useState<string | null>(null);
  // The day whose "add" sheet is open (click-a-day-to-add, spec §B). null = closed.
  const [addDay, setAddDay] = useState<Date | null>(null);

  const goPrev = () => setCursor((c) => (view === "month" ? addMonths(c, -1) : addDays(startOfWeek(c), -7)));
  const goNext = () => setCursor((c) => (view === "month" ? addMonths(c, 1) : addDays(startOfWeek(c), 7)));
  const goToday = () => {
    setCursor(TODAY);
    setSelected(TODAY);
  };

  const title = view === "month" ? monthTitle(cursor) : weekRangeTitle(cursor);

  // Count the weekly goals active in the currently-displayed window (the month's
  // grid weeks, or just the visible week) — drives the lime header chip.
  const visibleGoalCount = useMemo(() => {
    const weeks =
      view === "month"
        ? monthGrid(cursor).filter((_, i) => i % 7 === 0)
        : [startOfWeek(cursor)];
    return weeks.reduce(
      (sum, monday) => sum + weeklyGoalsOnWeek(data, monday).length,
      0,
    );
  }, [data, view, cursor]);

  const selectedSessions = useMemo(
    () => sessionsOnDay(data, selected),
    [data, selected],
  );
  const selectedActivities = useMemo(
    () => activitiesOnDay(data, selected),
    [data, selected],
  );
  const selectedMeals = useMemo(
    () => mealMarkersOnDay(data, selected),
    [data, selected],
  );

  // Re-derive the open session from the live feed each render: an inline edit
  // (optimistic) or a cross-device change (live-sync) updates data.sessions, so
  // the detail sheet stays in sync; a delete drops it and closes the sheet.
  const openSession = useMemo(
    () => data.sessions.find((s) => s.id === openSessionId) ?? null,
    [data.sessions, openSessionId],
  );
  const openActivity = useMemo(
    () => data.activities.find((a) => a.id === openActivityId) ?? null,
    [data.activities, openActivityId],
  );
  const openMeal = useMemo(
    () => data.mealMarkers.find((m) => m.id === openMealId) ?? null,
    [data.mealMarkers, openMealId],
  );
  const pickSession: PickSession = (s) => setOpenSessionId(s.id);
  const pickActivity: PickActivity = (a) => setOpenActivityId(a.id);
  const pickMeal: PickMeal = (m) => setOpenMealId(m.id);
  /** Open the add-on-day sheet (prefilled with that day's date). */
  const openAdd: AddOnDay = (d) => setAddDay(d);

  return (
    <Card padded={false} className="overflow-hidden">
      {/* ---- Calendar header: title + nav + view toggle ---- */}
      <div className="flex flex-col gap-4 border-b border-line p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-md bg-grad-coral text-white shadow-volt">
            <IconCalendarBlank />
          </span>
          <div>
            <h2 className="font-display text-xl font-extrabold leading-tight tracking-tightest">{title}</h2>
            <p className="text-sm text-body-soft">{t("dashboard:calendar.allClients")}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={goToday}>
            {t("dashboard:calendar.today")}
          </Button>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={goPrev}
              aria-label={view === "month" ? t("dashboard:calendar.prevMonth") : t("dashboard:calendar.prevWeek")}
              className="grid h-9 w-9 place-items-center rounded-pill bg-surface text-body-soft ring-1 ring-line transition-colors hover:bg-surface-sunken hover:text-body"
            >
              <IconChevronLeft />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label={view === "month" ? t("dashboard:calendar.nextMonth") : t("dashboard:calendar.nextWeek")}
              className="grid h-9 w-9 place-items-center rounded-pill bg-surface text-body-soft ring-1 ring-line transition-colors hover:bg-surface-sunken hover:text-body"
            >
              <IconChevronRight />
            </button>
          </div>
          <ViewToggle view={view} onChange={setView} />
        </div>
      </div>

      {/* ---- Legend (shared provenance key: PT session / homework / own) ---- */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4">
        <CalendarLegend />
        {visibleGoalCount > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-pill bg-grad-lime px-2.5 py-1 text-xs font-bold text-white shadow-sm">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white/90" aria-hidden />
            {t("weeklyGoals:banner.count", { count: visibleGoalCount })}
          </span>
        )}
      </div>

      {/* ---- The grid ---- */}
      <div className="p-3 sm:p-5">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${view}-${cursor.getFullYear()}-${cursor.getMonth()}-${view === "week" ? startOfWeek(cursor).getDate() : ""}`}
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
          >
            {view === "month" ? (
              <MonthView
                data={data}
                cursor={cursor}
                selected={selected}
                onSelect={setSelected}
                onPickSession={pickSession}
                onPickActivity={pickActivity}
                onPickMeal={pickMeal}
                onAdd={openAdd}
                trainerTz={trainerTz}
              />
            ) : (
              <WeekView
                data={data}
                cursor={cursor}
                selected={selected}
                onSelect={setSelected}
                onPickSession={pickSession}
                onPickActivity={pickActivity}
                onPickMeal={pickMeal}
                onAdd={openAdd}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ---- Selected-day detail ---- */}
      <DayDetail
        day={selected}
        sessions={selectedSessions}
        activities={selectedActivities}
        meals={selectedMeals}
        onPickSession={pickSession}
        onPickActivity={pickActivity}
        onPickMeal={pickMeal}
        onAdd={openAdd}
      />

      {/* ---- Appointment detail + inline edit (bottom sheet on phone) ---- */}
      <SessionDetailSheet
        session={openSession}
        onClose={() => setOpenSessionId(null)}
      />

      {/* ---- Activity detail (own / suggested) ---- */}
      <ActivityDetailSheet
        activity={openActivity}
        onClose={() => setOpenActivityId(null)}
        showClient
      />

      {/* ---- Meal day-marker summary (amber) ---- */}
      <MealMarkerSheet
        marker={openMeal}
        onClose={() => setOpenMealId(null)}
        showClient
      />

      {/* ---- Click-a-day-to-add: pick a client + create a session/goal/homework
              on the clicked day; the create-mutation invalidations live-update
              this grid (spec §B). ---- */}
      <AddEntrySheet day={addDay} onClose={() => setAddDay(null)} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// View toggle (segmented control, Pulse styling)
// ---------------------------------------------------------------------------
function ViewToggle({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  return (
    <div role="tablist" aria-label={t("dashboard:calendar.viewToggle")} className="inline-flex items-center gap-1 rounded-pill bg-surface-sunken p-1 ring-1 ring-line">
      {(["month", "week"] as View[]).map((v) => {
        const active = view === v;
        return (
          <button
            key={v}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(v)}
            className={cn(
              "relative inline-flex items-center gap-1.5 rounded-pill px-3.5 py-1.5 font-display text-sm font-bold capitalize transition-colors duration-fast",
              active ? "text-white" : "text-body-soft hover:text-body",
            )}
          >
            {active && (
              <motion.span
                layoutId="calendar-view-pill"
                transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 480, damping: 36 }}
                className="absolute inset-0 -z-10 rounded-pill bg-grad-coral shadow-volt"
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-1.5">
              <IconLayoutGrid width={14} height={14} />
              {t(`dashboard:calendar.${v}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MONTH VIEW
// ---------------------------------------------------------------------------
function MonthView({
  data,
  cursor,
  selected,
  onSelect,
  onPickSession,
  onPickActivity,
  onPickMeal,
  onAdd,
  trainerTz,
}: {
  data: ReturnType<typeof useDashboardCtx>;
  cursor: Date;
  selected: Date;
  onSelect: (d: Date) => void;
  onPickSession: PickSession;
  onPickActivity: PickActivity;
  onPickMeal: PickMeal;
  onAdd: AddOnDay;
  trainerTz: string | null;
}) {
  const { t } = useTranslation();
  const days = useMemo(() => monthGrid(cursor), [cursor]);
  const month = cursor.getMonth();
  // Localized Monday-start weekday labels (繁中 -> 週一…週日, en -> Mon…Sun).
  const labels = useMemo(() => weekdayLabels(), []);

  return (
    <div>
      <div className="mb-1.5 grid grid-cols-7 gap-1.5 sm:gap-2">
        {labels.map((w, i) => (
          <div key={i} className="px-1 text-center text-[0.68rem] font-bold uppercase tracking-kicker text-body-faint sm:text-xs">
            {/* Phone shows one glyph: CJK by last char (一…日), Latin by first (M). */}
            <span className="sm:hidden">{/[一-鿿]/.test(w) ? w.slice(-1) : w.slice(0, 1)}</span>
            <span className="hidden sm:inline">{w}</span>
          </div>
        ))}
      </div>
      {/* Six week rows; each carries a slim lime weekly-goal banner over the row
          when one or more clients have a coach goal set for that week (spec §1). */}
      <div className="space-y-1.5 sm:space-y-2">
        {Array.from({ length: 6 }, (_, w) => {
          const rowDays = days.slice(w * 7, w * 7 + 7);
          const monday = rowDays[0];
          const goals = monday ? weeklyGoalsOnWeek(data, monday) : [];
          return (
            <div key={`wk-${monday ? monday.toISOString() : w}`}>
              {goals.length > 0 && (
                <WeekGoalBanner goals={goals} label={t("weeklyGoals:banner.title")} />
              )}
              <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
                {rowDays.map((day) => (
                  <MonthCell
                    key={day.toISOString()}
                    data={data}
                    day={day}
                    inMonth={day.getMonth() === month}
                    isToday={isSameDay(day, TODAY)}
                    isSelected={isSameDay(day, selected)}
                    onSelect={onSelect}
                    onPickSession={onPickSession}
                    onPickActivity={onPickActivity}
                    onPickMeal={onPickMeal}
                    onAdd={onAdd}
                    trainerTz={trainerTz}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The slim lime week-level weekly-goal banner spanning a month week-row. Shows
 * the goal label + (on desktop) the client names whose goals are active that
 * week. Presentational only; client names are API data (not user-facing copy),
 * the LABEL is an i18n key.
 */
function WeekGoalBanner({
  goals,
  label,
}: {
  goals: WeeklyGoalBannerVM[];
  label: string;
}) {
  const names = goals
    .map((g) => g.clientName)
    .filter((n): n is string => !!n && n.trim() !== "");
  return (
    <div className="mb-1.5 flex items-center gap-1.5 rounded-pill bg-grad-lime px-3 py-1 text-white shadow-sm">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white/90" aria-hidden />
      <span className="shrink-0 font-display text-[0.7rem] font-bold tracking-tight sm:text-xs">
        {label}
      </span>
      {names.length > 0 && (
        <span className="hidden truncate text-[0.7rem] font-semibold text-white/90 sm:inline">
          {names.join(" · ")}
        </span>
      )}
      <span className="ml-auto shrink-0 rounded-pill bg-white/20 px-1.5 text-[0.65rem] font-bold tabular-nums">
        {goals.length}
      </span>
    </div>
  );
}

function MonthCell({
  data,
  day,
  inMonth,
  isToday,
  isSelected,
  onSelect,
  onPickSession,
  onPickActivity,
  onPickMeal,
  onAdd,
  trainerTz,
}: {
  data: ReturnType<typeof useDashboardCtx>;
  day: Date;
  inMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  onSelect: (d: Date) => void;
  onPickSession: PickSession;
  onPickActivity: PickActivity;
  onPickMeal: PickMeal;
  onAdd: AddOnDay;
  trainerTz: string | null;
}) {
  const { t } = useTranslation();
  // When the trainer has set a display timezone, re-filter from the full data
  // using tz-aware day matching. Falls back to device-local (current behavior)
  // when trainerTz is null — byte-identical to the previous implementation.
  const sessions = useMemo(() => {
    if (trainerTz) {
      const key = localDayInZone(day.toISOString(), trainerTz);
      return data.sessions
        .filter((s) => localDayInZone(s.startsAt, trainerTz) === key)
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    }
    return sessionsOnDay(data, day);
  }, [data, day, trainerTz]);
  const activities = useMemo(() => {
    if (trainerTz) {
      const key = localDayInZone(day.toISOString(), trainerTz);
      return data.activities
        .filter((a) => localDayInZone(a.startsAt, trainerTz) === key)
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    }
    return activitiesOnDay(data, day);
  }, [data, day, trainerTz]);
  const meals = useMemo(() => mealMarkersOnDay(data, day), [data, day]);
  // Merge sessions + activities + meal markers into one time-sorted list.
  const items = useMemo(
    () => mergeEntries(sessions, activities, meals),
    [sessions, activities, meals],
  );
  const shown = items.slice(0, 3);
  const extra = items.length - shown.length;

  // The cell is a role="button" (not a <button>) so the per-event chips inside
  // can be real, individually clickable buttons — nesting <button> is invalid.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(day)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(day);
        }
      }}
      aria-label={t("dashboard:calendar.dayLabel", { day: formatDayLong(day), count: items.length })}
      aria-pressed={isSelected}
      className={cn(
        "group relative flex min-h-[5.25rem] cursor-pointer flex-col rounded-md p-1.5 text-left transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt sm:min-h-[7rem] sm:p-2",
        inMonth ? "bg-surface ring-1 ring-line" : "bg-surface-sunken/40 ring-1 ring-line-soft",
        isSelected ? "ring-2 ring-coral shadow-volt" : "hover:-translate-y-0.5 hover:shadow-sm hover:ring-line-strong",
      )}
    >
      <div className="mb-1 flex items-center justify-between">
        <span
          className={cn(
            "grid h-6 min-w-[1.5rem] place-items-center rounded-pill px-1 text-xs font-bold tabular-nums",
            isToday
              ? "bg-grad-coral text-white shadow-volt"
              : inMonth
                ? "text-body"
                : "text-body-faint",
          )}
        >
          {day.getDate()}
        </span>
        <span className="flex items-center gap-1">
          {items.length > 0 && (
            <span className="rounded-pill bg-surface-sunken px-1.5 text-[0.6rem] font-bold text-body-soft sm:hidden">
              {items.length}
            </span>
          )}
          <AddDayButton day={day} onAdd={onAdd} />
        </span>
      </div>

      {/* Desktop/tablet: stacked event chips (each opens its detail). Phone: a
          dot row that's space-limited — the whole cell opens the day list, and
          tapping a session/activity there opens its detail. */}
      <div className="hidden flex-1 flex-col gap-1 sm:flex">
        {shown.map((e) =>
          e.entry === "session" ? (
            <EventChip
              key={`s${e.data.id}`}
              session={e.data}
              onPick={onPickSession}
            />
          ) : e.entry === "activity" ? (
            <ActivityChip
              key={`a${e.data.id}`}
              activity={e.data}
              onPick={onPickActivity}
            />
          ) : (
            <MealChip
              key={`m${e.data.id}`}
              marker={e.data}
              onPick={onPickMeal}
            />
          ),
        )}
        {extra > 0 && (
          <span className="mt-auto pl-0.5 text-[0.68rem] font-bold text-body-soft">{t("dashboard:calendar.moreCount", { count: extra })}</span>
        )}
      </div>
      <div className="mt-auto sm:hidden">
        <DaySummary
          items={toSummaryItems(items, t)}
          max={1}
          moreLabel={(n) => t("dashboard:calendar.moreCount", { count: n })}
        />
      </div>
    </div>
  );
}

/**
 * The "+" add affordance on a day cell. A real <button> (the cell is a
 * role="button", so this nests fine) that opens the add-on-day sheet for that
 * day, prefilled (spec §B). Always present but visually quiet — full opacity on
 * hover/focus of the cell — so the grid stays scannable. stopPropagation keeps
 * the click from also selecting the day.
 */
function AddDayButton({ day, onAdd }: { day: Date; onAdd: AddOnDay }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onAdd(day);
      }}
      aria-label={t("dashboard:add.addOnDayAria", { day: formatDayLong(day) })}
      className="grid h-6 w-6 shrink-0 place-items-center rounded-pill text-body-faint opacity-0 ring-1 ring-line transition-all duration-fast hover:bg-coral-soft hover:text-coral focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt group-hover:opacity-100"
    >
      <IconPlus width={13} height={13} />
    </button>
  );
}

function EventChip({
  session,
  onPick,
}: {
  session: SessionWithClient;
  onPick: PickSession;
}) {
  const { t } = useTranslation();
  const st = SESSION_STYLE[session.type];
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation(); // don't also trigger the day-cell select
        onPick(session);
      }}
      aria-label={`${formatTime(session.startsAt)} ${session.clientName} — ${t(`dashboard:sessionType.${session.type}`)}`}
      className={cn(
        "flex items-center gap-1 truncate rounded-sm px-1.5 py-0.5 text-left text-[0.7rem] font-bold leading-tight transition-transform duration-fast hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
        st.soft,
        st.text,
      )}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", st.dot)} />
      <span className="tabular-nums opacity-80">{formatTime(session.startsAt)}</span>
      <span className="truncate">{session.clientName.split(" ")[0]}</span>
    </button>
  );
}

/** A month-cell chip for a provenance-tagged activity (lime/violet). */
function ActivityChip({
  activity,
  onPick,
}: {
  activity: ActivityVM;
  onPick: PickActivity;
}) {
  const { t } = useTranslation();
  const st = ENTRY_STYLE[activity.kind];
  const label = activity.clientName.split(" ")[0] || activity.title || "";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onPick(activity);
      }}
      aria-label={`${formatTime(activity.startsAt)} ${label} — ${t(`activity:provenance.${activity.kind}`)}`}
      className={cn(
        "flex items-center gap-1 truncate rounded-sm px-1.5 py-0.5 text-left text-[0.7rem] font-bold leading-tight transition-transform duration-fast hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
        st.soft,
        st.text,
      )}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", st.dot)} />
      {activity.startsAt && (
        <span className="tabular-nums opacity-80">{formatTime(activity.startsAt)}</span>
      )}
      <span className="truncate">{label}</span>
    </button>
  );
}

/** A month-cell chip for a per-day meal marker (amber, total kcal). */
function MealChip({
  marker,
  onPick,
}: {
  marker: MealMarkerVM;
  onPick: PickMeal;
}) {
  const { t } = useTranslation();
  const st = ENTRY_STYLE.meal;
  const kcal = Math.round(marker.kcal);
  const name = marker.clientName?.split(" ")[0] || t("nutrition:marker.title");
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onPick(marker);
      }}
      aria-label={t("nutrition:marker.aria", { name, kcal })}
      className={cn(
        "flex items-center gap-1 truncate rounded-sm px-1.5 py-0.5 text-left text-[0.7rem] font-bold leading-tight transition-transform duration-fast hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
        st.soft,
        st.text,
      )}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", st.dot)} />
      <span className="truncate">{name}</span>
      <span className="ml-auto shrink-0 tabular-nums opacity-80">{kcal}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// WEEK VIEW
// ---------------------------------------------------------------------------
function WeekView({
  data,
  cursor,
  selected,
  onSelect,
  onPickSession,
  onPickActivity,
  onPickMeal,
  onAdd,
}: {
  data: ReturnType<typeof useDashboardCtx>;
  cursor: Date;
  selected: Date;
  onSelect: (d: Date) => void;
  onPickSession: PickSession;
  onPickActivity: PickActivity;
  onPickMeal: PickMeal;
  onAdd: AddOnDay;
}) {
  const { t } = useTranslation();
  const days = useMemo(() => weekDays(cursor), [cursor]);
  const goals = useMemo(
    () => weeklyGoalsOnWeek(data, startOfWeek(cursor)),
    [data, cursor],
  );
  return (
    <div>
      {goals.length > 0 && (
        <WeekGoalBanner goals={goals} label={t("weeklyGoals:banner.title")} />
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {days.map((day) => (
          <WeekColumn
            key={day.toISOString()}
            data={data}
            day={day}
            isToday={isSameDay(day, TODAY)}
            isSelected={isSameDay(day, selected)}
            onSelect={onSelect}
            onPickSession={onPickSession}
            onPickActivity={onPickActivity}
            onPickMeal={onPickMeal}
            onAdd={onAdd}
          />
        ))}
      </div>
    </div>
  );
}

function WeekColumn({
  data,
  day,
  isToday,
  isSelected,
  onSelect,
  onPickSession,
  onPickActivity,
  onPickMeal,
  onAdd,
}: {
  data: ReturnType<typeof useDashboardCtx>;
  day: Date;
  isToday: boolean;
  isSelected: boolean;
  onSelect: (d: Date) => void;
  onPickSession: PickSession;
  onPickActivity: PickActivity;
  onPickMeal: PickMeal;
  onAdd: AddOnDay;
}) {
  const { t } = useTranslation();
  const sessions = useMemo(() => sessionsOnDay(data, day), [data, day]);
  const activities = useMemo(() => activitiesOnDay(data, day), [data, day]);
  const meals = useMemo(() => mealMarkersOnDay(data, day), [data, day]);
  const items = useMemo(
    () => mergeEntries(sessions, activities, meals),
    [sessions, activities, meals],
  );
  // role="button" (not <button>) so the events inside can be real buttons.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(day)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(day);
        }
      }}
      aria-label={t("dashboard:calendar.dayLabel", { day: formatDayLong(day), count: items.length })}
      aria-pressed={isSelected}
      className={cn(
        "group flex min-h-[10rem] cursor-pointer flex-col rounded-md p-2 text-left ring-1 transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
        isSelected ? "ring-2 ring-coral shadow-volt" : "ring-line hover:-translate-y-0.5 hover:shadow-sm",
        isToday ? "bg-coral-soft/40" : "bg-surface",
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[0.66rem] font-bold uppercase tracking-kicker text-body-faint">
          {weekdayLabel(day)}
        </span>
        <span className="flex items-center gap-1">
          <AddDayButton day={day} onAdd={onAdd} />
          <span
            className={cn(
              "grid h-6 min-w-[1.5rem] place-items-center rounded-pill px-1 text-sm font-extrabold tabular-nums",
              isToday ? "bg-grad-coral text-white shadow-volt" : "text-body",
            )}
          >
            {day.getDate()}
          </span>
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-1.5">
        {items.length === 0 ? (
          <span className="mt-2 text-[0.7rem] text-body-faint">{t("calendar.noSessions")}</span>
        ) : (
          items.map((e) =>
            e.entry === "session" ? (
              <WeekEvent key={`s${e.data.id}`} session={e.data} onPick={onPickSession} />
            ) : e.entry === "activity" ? (
              <WeekActivityEvent
                key={`a${e.data.id}`}
                activity={e.data}
                onPick={onPickActivity}
              />
            ) : (
              <WeekMealEvent
                key={`m${e.data.id}`}
                marker={e.data}
                onPick={onPickMeal}
              />
            ),
          )
        )}
      </div>
    </div>
  );
}

function WeekEvent({
  session,
  onPick,
}: {
  session: SessionWithClient;
  onPick: PickSession;
}) {
  const { t } = useTranslation();
  const st = SESSION_STYLE[session.type];
  const typeLabel = t(`dashboard:sessionType.${session.type}`);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation(); // don't also select the day column
        onPick(session);
      }}
      aria-label={`${formatTime(session.startsAt)} ${session.clientName} — ${typeLabel}`}
      className={cn(
        "flex w-full flex-col rounded-sm border-l-[3px] px-2 py-1 text-left transition-transform duration-fast hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
        st.soft,
      )}
      style={{ borderColor: "currentColor" }}
    >
      <span className="flex items-center justify-between gap-1">
        <span className="truncate text-xs font-extrabold text-body">{session.clientName.split(" ")[0]}</span>
        <span className={cn("shrink-0 text-[0.66rem] font-bold tabular-nums", st.text)}>{formatTime(session.startsAt)}</span>
      </span>
      <span className={cn("text-[0.66rem] font-bold", st.text)}>{typeLabel}</span>
    </button>
  );
}

/** A week-column event for a provenance-tagged activity (lime/violet). */
function WeekActivityEvent({
  activity,
  onPick,
}: {
  activity: ActivityVM;
  onPick: PickActivity;
}) {
  const { t } = useTranslation();
  const st = ENTRY_STYLE[activity.kind];
  const label = t(`activity:provenance.${activity.kind}`);
  const name = activity.clientName.split(" ")[0] || activity.title || label;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onPick(activity);
      }}
      aria-label={`${formatTime(activity.startsAt)} ${name} — ${label}`}
      className={cn(
        "flex w-full flex-col rounded-sm border-l-[3px] px-2 py-1 text-left transition-transform duration-fast hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
        st.soft,
      )}
      style={{ borderColor: "currentColor" }}
    >
      <span className="flex items-center justify-between gap-1">
        <span className="truncate text-xs font-extrabold text-body">{name}</span>
        {activity.startsAt && (
          <span className={cn("shrink-0 text-[0.66rem] font-bold tabular-nums", st.text)}>{formatTime(activity.startsAt)}</span>
        )}
      </span>
      <span className={cn("text-[0.66rem] font-bold", st.text)}>{label}</span>
    </button>
  );
}

/** A week-column event for a per-day meal marker (amber, total kcal). */
function WeekMealEvent({
  marker,
  onPick,
}: {
  marker: MealMarkerVM;
  onPick: PickMeal;
}) {
  const { t } = useTranslation();
  const st = ENTRY_STYLE.meal;
  const kcal = Math.round(marker.kcal);
  const name = marker.clientName?.split(" ")[0] || t("nutrition:marker.title");
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onPick(marker);
      }}
      aria-label={t("nutrition:marker.aria", { name, kcal })}
      className={cn(
        "flex w-full flex-col rounded-sm border-l-[3px] px-2 py-1 text-left transition-transform duration-fast hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
        st.soft,
      )}
      style={{ borderColor: "currentColor" }}
    >
      <span className="flex items-center justify-between gap-1">
        <span className="truncate text-xs font-extrabold text-body">{name}</span>
        <span className={cn("shrink-0 text-[0.66rem] font-bold tabular-nums", st.text)}>
          {kcal}
        </span>
      </span>
      <span className={cn("text-[0.66rem] font-bold", st.text)}>
        {t("nutrition:marker.title")}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// SELECTED-DAY DETAIL
// ---------------------------------------------------------------------------
function DayDetail({
  day,
  sessions,
  activities,
  meals,
  onPickSession,
  onPickActivity,
  onPickMeal,
  onAdd,
}: {
  day: Date;
  sessions: SessionWithClient[];
  activities: ActivityVM[];
  meals: MealMarkerVM[];
  onPickSession: PickSession;
  onPickActivity: PickActivity;
  onPickMeal: PickMeal;
  onAdd: AddOnDay;
}) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const total = sessions.reduce((s, x) => s + x.durationMin, 0);
  const merged = useMemo(
    () => mergeEntries(sessions, activities, meals),
    [sessions, activities, meals],
  );
  return (
    <div className="border-t border-line bg-surface-soft p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="min-w-0 truncate font-display text-base font-extrabold tracking-tight">
          {isSameDay(day, TODAY) ? t("dashboard:calendar.todayPrefix") : ""}
          {formatDayLong(day)}
        </h3>
        <div className="flex shrink-0 items-center gap-3">
          {sessions.length > 0 && (
            <span className="hidden text-sm font-bold text-body-soft tabular-nums sm:inline">
              {t("dashboard:calendar.daySummary", { count: sessions.length, hours: (total / 60).toFixed(total % 60 === 0 ? 0 : 1) })}
            </span>
          )}
          {/* Always-visible primary add entry point for the selected day (§B). */}
          <Button
            size="sm"
            iconLeft={<IconPlus width={16} height={16} />}
            onClick={() => onAdd(day)}
          >
            {t("dashboard:add.addButton")}
          </Button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={day.toISOString()}
          initial={reduce ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={{ duration: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
        >
          {merged.length === 0 ? (
            <p className="rounded-md bg-surface px-4 py-6 text-center text-sm text-body-muted ring-1 ring-line">
              {t("dashboard:calendar.noSessionsDay")}
            </p>
          ) : (
            <ul className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {merged.map((e) =>
                e.entry === "session" ? (
                  <DayRow key={`s${e.data.id}`} session={e.data} onPick={onPickSession} />
                ) : e.entry === "activity" ? (
                  <ActivityDayRow
                    key={`a${e.data.id}`}
                    activity={e.data}
                    onPick={onPickActivity}
                  />
                ) : (
                  <MealDayRow
                    key={`m${e.data.id}`}
                    marker={e.data}
                    onPick={onPickMeal}
                  />
                ),
              )}
            </ul>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function DayRow({
  session,
  onPick,
}: {
  session: SessionWithClient;
  onPick: PickSession;
}) {
  const { t } = useTranslation();
  const st = SESSION_STYLE[session.type];
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(session)}
        className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm hover:ring-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      >
        <span className={cn("h-12 w-1.5 shrink-0 rounded-pill", st.grad)} />
        <Avatar name={session.clientName} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">{session.clientName}</span>
            <Badge tone={st.tone} className="shrink-0">{t(`dashboard:sessionType.${session.type}`)}</Badge>
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-body-soft">
            <span className="font-bold tabular-nums text-body-muted">
              {formatTime(session.startsAt)} · {t("dashboard:calendar.durationShort", { count: session.durationMin })}
            </span>
            {session.location && (
              <span className="inline-flex min-w-0 items-center gap-1 truncate">
                <IconMapPin width={13} height={13} />
                <span className="truncate">{session.location}</span>
              </span>
            )}
          </div>
        </div>
        <Badge tone={session.status === "completed" ? "success" : "neutral"} dot className="shrink-0">
          {session.status === "completed" ? t("dashboard:calendar.statusDone") : t("dashboard:calendar.statusPlanned")}
        </Badge>
      </button>
    </li>
  );
}

/** A selected-day row for a provenance-tagged activity (lime/violet). */
function ActivityDayRow({
  activity,
  onPick,
}: {
  activity: ActivityVM;
  onPick: PickActivity;
}) {
  const { t } = useTranslation();
  const st = ENTRY_STYLE[activity.kind];
  const provenance = t(`activity:provenance.${activity.kind}`);
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(activity)}
        className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm hover:ring-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      >
        <span className={cn("h-12 w-1.5 shrink-0 rounded-pill", st.grad)} />
        {activity.clientName ? (
          <Avatar name={activity.clientName} size="md" />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {activity.clientName || activity.title || provenance}
            </span>
            <Badge tone={st.tone} className="shrink-0">{provenance}</Badge>
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-body-soft">
            <span className="font-bold tabular-nums text-body-muted">
              {activity.startsAt ? formatTime(activity.startsAt) : ""}
              {activity.type ? ` · ${activity.type}` : ""}
            </span>
          </div>
        </div>
        <Badge tone={activity.status === "completed" ? "success" : "neutral"} dot className="shrink-0">
          {activity.status === "completed"
            ? t("activity:detail.statusCompleted")
            : t("activity:detail.statusPlanned")}
        </Badge>
      </button>
    </li>
  );
}

/** A selected-day row for a per-day meal marker (amber, total kcal + count). */
function MealDayRow({
  marker,
  onPick,
}: {
  marker: MealMarkerVM;
  onPick: PickMeal;
}) {
  const { t } = useTranslation();
  const st = ENTRY_STYLE.meal;
  const kcal = Math.round(marker.kcal);
  const label = t("nutrition:marker.title");
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(marker)}
        className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm hover:ring-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      >
        <span className={cn("h-12 w-1.5 shrink-0 rounded-pill", st.grad)} />
        {marker.clientName ? <Avatar name={marker.clientName} size="md" /> : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {marker.clientName || label}
            </span>
            <Badge tone={st.tone} className="shrink-0">{label}</Badge>
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-body-soft">
            <span className="font-bold tabular-nums text-body-muted">
              {marker.count > 0
                ? t("nutrition:marker.mealCount", { count: marker.count })
                : label}
            </span>
          </div>
        </div>
        <span className="shrink-0 font-display text-sm font-extrabold tabular-nums text-body">
          {kcal.toLocaleString()}{" "}
          <span className="text-xs font-bold text-body-soft">
            {t("nutrition:macro.kcalUnit")}
          </span>
        </span>
      </button>
    </li>
  );
}
